import React, { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { ShieldCheck, ShieldAlert, Ban, ShoppingBasket, Loader2, Plus, QrCode, Tag } from "lucide-react";
import { NutrientBadge } from "@/components/NutrientPanel";
import { toast } from "sonner";

const STATUS = { listed: "#10B981", sold_out: "#64748B", blocked: "#EF4444", recalled: "#EF4444" };
const PRODUCER = ["farmer", "cooperative", "collective_executive", "admin"];

const Provenance = ({ p }) => {
  if (!p) return <span className="text-[10px] font-mono text-slate-500 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> no scan provenance</span>;
  const ok = p.mrl_exceedances === 0;
  return (
    <span className={`text-[10px] font-mono flex items-center gap-1 ${ok ? "text-[#00FF7F]" : "text-[#EF4444]"}`} title={p.scan_hash}>
      {ok ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />} scan {p.scan_hash?.slice(0, 10)} · {p.residue_risk} · NDVI {p.ndvi}
    </span>
  );
};

const LotForm = ({ coops, onCreated }) => {
  const [f, setF] = useState({ coop_id: coops[0]?.id || "", product: "Wheat", quantity_kg: 1000, unit_price_try: 12, harvest_date: "2026-07-15", scan_id: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/commons/lots", { ...f, quantity_kg: +f.quantity_kg, unit_price_try: +f.unit_price_try, scan_id: f.scan_id || null });
      toast.success(data.anomaly ? `Lot listed — anomaly flagged: ${data.anomaly.type}` : `Lot listed (${data.status})`);
      onCreated();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const cls = "bg-[#050A15] border border-white/10 px-2 py-1.5 text-xs font-mono outline-none focus:border-[#00FF7F] w-full";
  return (
    <form onSubmit={submit} className="qs-panel p-4 grid md:grid-cols-6 gap-2 items-end" data-testid="lot-form">
      <select data-testid="lot-coop" className={cls} value={f.coop_id} onChange={upd("coop_id")}>{coops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
      <input data-testid="lot-product" className={cls} value={f.product} onChange={upd("product")} placeholder="Product" />
      <input data-testid="lot-qty" className={cls} type="number" value={f.quantity_kg} onChange={upd("quantity_kg")} placeholder="kg" />
      <input data-testid="lot-price" className={cls} type="number" step="0.1" value={f.unit_price_try} onChange={upd("unit_price_try")} placeholder="TRY/kg" />
      <input data-testid="lot-scan" className={cls} value={f.scan_id} onChange={upd("scan_id")} placeholder="scan id (optional)" />
      <button data-testid="lot-submit" disabled={busy} className="qs-cta bg-[#00FF7F] text-[#050A15] font-semibold px-3 py-1.5 text-xs flex items-center justify-center gap-1">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5" /> List lot</>}
      </button>
    </form>
  );
};

export const LotsMarket = ({ lots, coops, onChange }) => {
  const { user } = useAuth();
  const [ordering, setOrdering] = useState(null);
  const canList = user && PRODUCER.includes(user.role) && coops.some((c) => c.owner_id === user.id || ["admin", "collective_executive"].includes(user.role));

  const order = async (lot) => {
    const qty = prompt(`Order ${lot.product} from ${lot.coop_name} — kg (max ${lot.available_kg}) @ ${lot.unit_price_try} TRY/kg:`, Math.min(100, lot.available_kg));
    if (!qty) return;
    setOrdering(lot.id);
    try {
      const { data } = await api.post("/commons/orders", { lot_id: lot.id, quantity_kg: +qty });
      toast.success(`Paid ${data.total_try} TRY · balance ${data.buyer_balance_try} TRY · tx ${data.tx_hash.slice(0, 12)}`);
      onChange();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); } finally { setOrdering(null); }
  };

  return (
    <div className="space-y-4" data-testid="lots-market">
      {canList && <LotForm coops={coops.filter((c) => c.owner_id === user.id || ["admin", "collective_executive"].includes(user.role))} onCreated={onChange} />}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {lots.map((l) => (
          <div key={l.id} data-testid={`lot-${l.id}`} className="qs-panel qs-lift p-4 flex flex-col gap-2">
            <div className="flex justify-between items-start">
              <div><div className="font-display font-semibold">{l.product}</div><div className="text-xs text-slate-500">{l.coop_name} · {l.city}</div></div>
              <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 border" style={{ color: STATUS[l.status], borderColor: STATUS[l.status] + "66" }}>{l.status.replace("_", " ")}</span>
            </div>
            <div className="flex justify-between font-mono text-sm"><span className="text-[#00FFFF]">{l.unit_price_try} TRY/kg</span><span className="text-slate-400">{l.available_kg} / {l.quantity_kg} kg</span></div>
            <Provenance p={l.provenance} />
            <NutrientBadge np={l.provenance?.nutrient_passport} />
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] font-mono text-slate-600 truncate">anchor {l.anchor}</div>
              <Link to={`/passport/${l.id}`} data-testid={`passport-link-${l.id}`} title="Open QR passport" className="shrink-0 flex items-center gap-1 text-[10px] text-[#00FFFF] hover:underline">
                <img src={`${API}/commons/passport/${l.id}/qr.png`} alt="QR" className="h-9 w-9 bg-white p-0.5" loading="lazy" /> <QrCode className="h-3 w-3" /> passport
              </Link>
              <a href={`${API}/commons/passport/${l.id}/label.pdf`} target="_blank" rel="noreferrer" data-testid={`label-link-${l.id}`} title="Print stall label" className="shrink-0 flex items-center gap-1 text-[10px] text-[#F59E0B] hover:underline"><Tag className="h-3 w-3" /> label</a>
            </div>
            {l.status === "listed" ? (
              <button data-testid={`order-${l.id}`} onClick={() => user ? order(l) : toast.info("Sign in to order")} disabled={ordering === l.id}
                className="mt-1 flex items-center justify-center gap-2 border border-[#00FF7F]/50 text-[#00FF7F] py-1.5 text-xs hover:bg-[#00FF7F]/10">
                {ordering === l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ShoppingBasket className="h-3.5 w-3.5" /> Order direct (wallet)</>}
              </button>
            ) : <div className="mt-1 flex items-center justify-center gap-2 text-xs text-slate-500 py-1.5 border border-white/5"><Ban className="h-3.5 w-3.5" /> Sale prohibited</div>}
          </div>
        ))}
        {!lots.length && <p className="text-sm text-slate-500">No lots yet.</p>}
      </div>
    </div>
  );
};
